import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';

const SupplierRequestFilters = ({ filters, onFiltersChange, onClearFilters }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchText, setSearchText] = useState(filters.search || '');

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'SUBMITTED', label: 'Submitted' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REJECTED', label: 'Rejected' }
  ];

  // Debounce search to avoid re-fetch on every keystroke
  const debounceTimeoutRef = useRef(null);
  
  // Keep local search text in sync when filters reset/clear externally
  useEffect(() => {
    setSearchText(filters.search || '');
  }, [filters.search]);

  const handleFilterChange = (key, value) => {
    if (key === 'search') {
      setSearchText(value);
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      const nextFilters = { ...filters, search: value };
      debounceTimeoutRef.current = setTimeout(() => {
        onFiltersChange(nextFilters);
      }, 1000);
      return;
    }
    onFiltersChange({ ...filters, [key]: value });
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => {
      if (!value || value === '') return false;
      return true;
    }).length;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <Card className="border border-slate-200 shadow-sm bg-white">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <Icon name="Filter" size={20} className="text-slate-700" />
            </div>
            <CardTitle className="text-lg font-semibold text-slate-900">Filters</CardTitle>
            {activeFiltersCount > 0 && (
              <Badge variant="default" className="ml-2">
                {activeFiltersCount}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeFiltersCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={onClearFilters}
                iconName="X"
                iconPosition="left"
              >
                Clear All
              </Button>
            )}
           
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {/* Quick Filters - Always Visible */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input
            type="search"
            placeholder="Search by vendor name, email, contact, or user..."
            value={searchText}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="w-full"
          />
          
          <Select
            placeholder="Select status"
            options={statusOptions}
            value={filters.status || ''}
            onChange={(value) => handleFilterChange('status', value)}
          />
        </div>

        {/* Active Filter Chips */}
        {activeFiltersCount > 0 && (
          <div className="border-t border-slate-200 pt-4 mt-4">
            <div className="flex flex-wrap gap-2">
              {Object.entries(filters).map(([key, value]) => {
                if (!value || value === '') return null;
                
                const getFilterLabel = (key, value) => {
                  switch (key) {
                    case 'search': return `Search: "${value}"`;
                    case 'status': return `Status: ${statusOptions.find(opt => opt.value === value)?.label || value}`;
                    default: return `${key}: ${value}`;
                  }
                };

                return (
                  <Badge
                    key={key}
                    variant="secondary"
                    className="flex items-center gap-1.5 pr-1"
                  >
                    <span>{getFilterLabel(key, value)}</span>
                    <button
                      onClick={() => handleFilterChange(key, '')}
                      className="hover:bg-slate-200 rounded-full p-0.5 transition-all ml-1"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </Badge>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SupplierRequestFilters;











